import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { RefreshCw, Search, Package, AlertTriangle, Save, ImageIcon } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CAT_COLORS = {
  jeux: '#5DBCBF', gourmand: '#C7A24C', buvette: '#1F9D7A', photo: '#7C5CBF',
  son: '#E0697A', deco: '#D98E5A', confort: '#5A8FD9', services: '#9CA3AF',
};

function Toggle({ on, onClick, disabled, testId }) {
  return (
    <button onClick={onClick} disabled={disabled} data-testid={testId}
      style={{
        width: 46, height: 26, borderRadius: 13, border: 'none', cursor: disabled ? 'wait' : 'pointer',
        background: on ? 'var(--admin-teal)' : 'var(--admin-surface2)',
        position: 'relative', transition: 'background .2s', flexShrink: 0,
      }}>
      <span style={{
        position: 'absolute', top: 3, left: on ? 23 : 3, width: 20, height: 20, borderRadius: '50%',
        background: on ? '#052b25' : 'var(--admin-faint)', transition: 'left .2s',
      }} />
    </button>
  );
}

export default function AdminDispoProduits() {
  const [groupes, setGroupes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [search, setSearch] = useState('');
  const [supplement, setSupplement] = useState(0);
  const [supplementInput, setSupplementInput] = useState('0');
  const [savingSupp, setSavingSupp] = useState(false);

  const token = sessionStorage.getItem('ose_auth') || localStorage.getItem('ose_auth');
  const H = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, cfg] = await Promise.all([
        fetch(`${BACKEND}/api/admin/catalogue/disponibilites`, { headers: H }).then(r => r.json()),
        fetch(`${BACKEND}/api/pack-config`).then(r => r.json()),
      ]);
      setGroupes(cat.groupes || []);
      setSupplement(cfg.supplement_impressions || 0);
      setSupplementInput(String(cfg.supplement_impressions || 0));
    } catch {
      toast.error('Erreur de chargement');
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  async function toggle(prod) {
    setProcessing(prod.id);
    try {
      const res = await fetch(`${BACKEND}/api/admin/catalogue/produits/${prod.id}/toggle`, { method: 'POST', headers: H });
      const data = await res.json();
      if (!res.ok) throw new Error();
      setGroupes(prev => prev.map(g => ({
        ...g,
        produits: g.produits.map(p => p.id === prod.id ? { ...p, disponible: data.disponible } : p),
      })));
      toast.success(`${prod.nom} — ${data.disponible ? 'remis en service' : 'mis hors service'}`);
    } catch {
      toast.error('Erreur');
    }
    setProcessing(null);
  }

  async function saveSupplement() {
    setSavingSupp(true);
    try {
      const res = await fetch(`${BACKEND}/api/admin/pack-config`, {
        method: 'PUT', headers: H,
        body: JSON.stringify({ supplement_impressions: parseFloat(supplementInput) || 0 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error();
      setSupplement(data.supplement_impressions);
      toast.success('Supplément impressions enregistré');
    } catch {
      toast.error('Erreur');
    }
    setSavingSupp(false);
  }

  const allProds = groupes.flatMap(g => g.produits);
  const nbHors = allProds.filter(p => !p.disponible).length;
  const nbTotal = allProds.length;

  const filteredGroups = groupes
    .map(g => ({
      ...g,
      produits: g.produits.filter(p =>
        !search || [p.nom, p.reference, p.famille].some(v => v?.toLowerCase().includes(search.toLowerCase()))),
    }))
    .filter(g => g.produits.length > 0);

  return (
    <div data-testid="admin-dispo-produits">
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <p className="adm-eyebrow">Produits</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginTop: 4 }}>
          <h2 style={{ fontFamily: 'var(--admin-font-display)', fontSize: 26, fontWeight: 700, letterSpacing: '-.6px', color: 'var(--admin-text)' }}>
            Disponibilités matériel
          </h2>
          <button onClick={load}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 10,
              border: '1px solid var(--admin-border)', background: 'var(--admin-surface)', color: 'var(--admin-muted)',
              fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
            <RefreshCw size={13}/> Actualiser
          </button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--admin-muted)', marginTop: 8, maxWidth: 640, lineHeight: 1.6 }}>
          Basculez un produit « hors service » (cassé, en réparation, retiré) : les packs qui en dépendent
          passent automatiquement en indisponible sur le site. Aucune date n'est concernée ici.
        </p>
      </div>

      {/* Stats + supplément */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 12, marginBottom: 18 }}>
        <div className="adm-card" style={{ padding: '14px 16px' }}>
          <p style={{ fontSize: 11, color: 'var(--admin-faint)', textTransform: 'uppercase', letterSpacing: '.05em' }}>En service</p>
          <p style={{ fontSize: 24, fontWeight: 700, color: 'var(--admin-teal)' }}>{nbTotal - nbHors}<span style={{ fontSize: 14, color: 'var(--admin-faint)' }}> / {nbTotal}</span></p>
        </div>
        <div className="adm-card" style={{ padding: '14px 16px' }}>
          <p style={{ fontSize: 11, color: 'var(--admin-faint)', textTransform: 'uppercase', letterSpacing: '.05em' }}>Hors service</p>
          <p style={{ fontSize: 24, fontWeight: 700, color: nbHors > 0 ? '#E0697A' : 'var(--admin-muted)' }}>{nbHors}</p>
        </div>
        <div className="adm-card" style={{ padding: '14px 16px' }}>
          <p style={{ fontSize: 11, color: 'var(--admin-faint)', textTransform: 'uppercase', letterSpacing: '.05em', display: 'flex', alignItems: 'center', gap: 5 }}>
            <ImageIcon size={12}/> Supplément impressions (photobooth)
          </p>
          <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
            <input type="number" value={supplementInput} onChange={e => setSupplementInput(e.target.value)}
              data-testid="supplement-impressions-input"
              style={{ width: 80, padding: '6px 9px', borderRadius: 8, background: 'var(--admin-surface2)',
                border: '1px solid var(--admin-border)', color: 'var(--admin-text)', fontSize: 14, fontWeight: 700, outline: 'none' }} />
            <span style={{ color: 'var(--admin-muted)', fontSize: 13 }}>€</span>
            <button onClick={saveSupplement} disabled={savingSupp || parseFloat(supplementInput) === supplement}
              data-testid="supplement-save-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 8, border: 'none',
                background: 'var(--admin-teal)', color: '#052b25', fontSize: 12, fontWeight: 700,
                cursor: 'pointer', opacity: (savingSupp || parseFloat(supplementInput) === supplement) ? 0.5 : 1 }}>
              <Save size={12}/> OK
            </button>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div style={{ position: 'relative', marginBottom: 16, maxWidth: 380 }}>
        <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-faint)' }} />
        <input placeholder="Rechercher un produit, une référence…" value={search} onChange={e => setSearch(e.target.value)}
          data-testid="dispo-search"
          style={{ width: '100%', padding: '9px 12px 9px 34px', borderRadius: 10, background: 'var(--admin-surface2)',
            border: '1px solid var(--admin-border)', color: 'var(--admin-text)', fontSize: 13, outline: 'none' }} />
      </div>

      {loading ? (
        <p style={{ color: 'var(--admin-muted)', padding: 20 }}>Chargement…</p>
      ) : filteredGroups.length === 0 ? (
        <div className="adm-card" style={{ padding: 40, textAlign: 'center' }}>
          <Package size={40} style={{ color: 'var(--admin-faint)', margin: '0 auto 12px' }} />
          <p style={{ color: 'var(--admin-muted)' }}>Aucun produit</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {filteredGroups.map(g => {
            const color = CAT_COLORS[g.categorie] || '#9CA3AF';
            return (
              <div key={g.categorie}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--admin-text)' }}>{g.label}</h3>
                  <span style={{ fontSize: 12, color: 'var(--admin-faint)' }}>({g.produits.length})</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 8 }}>
                  {g.produits.map(p => (
                    <div key={p.id} className="adm-card"
                      style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10,
                        opacity: p.disponible ? 1 : 0.6 }}
                      data-testid={`dispo-produit-${p.id}`}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--admin-text)' }}>
                          {p.nom} {p.sur_devis && <span style={{ fontSize: 10.5, color: 'var(--admin-faint)' }}>· sur devis</span>}
                        </p>
                        <p style={{ fontSize: 11, color: 'var(--admin-faint)' }}>
                          {p.reference || '—'}{p.prix ? ` · ${p.prix}€` : ''}
                          {p.packs_impactes > 0 && (
                            <span style={{ color: color }}> · {p.packs_impactes} pack{p.packs_impactes > 1 ? 's' : ''}</span>
                          )}
                        </p>
                      </div>
                      {!p.disponible && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#E0697A', display: 'flex', alignItems: 'center', gap: 3 }}>
                          <AlertTriangle size={11}/> HS
                        </span>
                      )}
                      <Toggle on={p.disponible} disabled={processing === p.id}
                        onClick={() => toggle(p)} testId={`dispo-toggle-${p.id}`} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
